import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement).value;
    if (message.length > 500) return;

    setFormStatus('submitting');
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);

    try {
      const res = await fetch('https://readdy.ai/api/form/d6hfmot4ieqv1pmm58cg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setFormStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setFormStatus('error');
      }
    } catch {
      setFormStatus('error');
    }
  };

  const officers = [
    {
      name: 'JD Hurd',
      title: 'Football President',
      image: 'https://static.readdy.ai/image/ad8804c8cf5327b346b1700de5ce7056/32dfb1fb181a86b6035f426f8d16e26b.jpeg'
    },
    {
      name: 'Paige Dickens',
      title: 'Cheer President',
      image: 'https://static.readdy.ai/image/ad8804c8cf5327b346b1700de5ce7056/b74a04f533031f183fbfbc1c7360be22.jpeg'
    },
    {
      name: 'Josh Bagby',
      title: 'Football Vice President',
      image: 'https://static.readdy.ai/image/ad8804c8cf5327b346b1700de5ce7056/989adf79f92bac04e7bb8b49ac0b18de.jpeg'
    },
    {
      name: 'Steve Canty',
      title: 'Safety Director',
      image: 'https://static.readdy.ai/image/ad8804c8cf5327b346b1700de5ce7056/8d197caebd69e0245870106cb7d27406.jpeg'
    }
  ];

  return (
    <footer id="contact" className="bg-warrior-gray py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">

        {/* Contact Form Card */}
        <div className="bg-white rounded-[32px] p-8 lg:p-12 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-48 h-48 bg-warrior-red/5 rounded-bl-full"></div>
          <div className="relative z-10 max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="font-display text-4xl text-warrior-black mb-2">HAVE A QUESTION?</h2>
              <p className="font-sans text-gray-600 text-sm">
                Reach us directly at{' '}
                <a href="mailto:contact@cherokeeyouth.com" className="text-warrior-red font-semibold hover:underline cursor-pointer">
                  contact@cherokeeyouth.com
                </a>{' '}
                or fill out the form below and we'll get back to you.
              </p>
            </div>

            {formStatus === 'success' ? (
              <div className="text-center py-10">
                <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                  <i className="ri-check-line text-3xl text-green-600"></i>
                </div>
                <h3 className="font-display text-2xl text-warrior-black mb-2">MESSAGE SENT!</h3>
                <p className="font-sans text-gray-600 text-sm">We'll be in touch soon. Go Warriors! 🏈</p>
                <button
                  onClick={() => setFormStatus('idle')}
                  className="mt-6 px-6 py-2 bg-warrior-red text-white font-sans text-sm rounded-md hover:bg-warrior-red/90 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Send Another
                </button>
              </div>
            ) : (
              <form
                data-readdy-form
                id="contact-question-form"
                onSubmit={handleSubmit}
                className="space-y-4"
              >
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block font-sans text-xs font-semibold text-gray-700 mb-1 uppercase tracking-wide">
                      Full Name <span className="text-warrior-red">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="Jane Smith"
                      className="w-full border border-gray-200 rounded-md px-4 py-2.5 font-sans text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-warrior-red/30 focus:border-warrior-red transition"
                    />
                  </div>
                  <div>
                    <label className="block font-sans text-xs font-semibold text-gray-700 mb-1 uppercase tracking-wide">
                      Email <span className="text-warrior-red">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="jane@example.com"
                      className="w-full border border-gray-200 rounded-md px-4 py-2.5 font-sans text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-warrior-red/30 focus:border-warrior-red transition"
                    />
                  </div>
                </div>

                <div>
                  <label className="block font-sans text-xs font-semibold text-gray-700 mb-1 uppercase tracking-wide">
                    Subject
                  </label>
                  <select
                    name="subject"
                    className="w-full border border-gray-200 rounded-md px-4 py-2.5 font-sans text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-warrior-red/30 focus:border-warrior-red transition cursor-pointer"
                  >
                    <option value="General Question">General Question</option>
                    <option value="Registration">Registration</option>
                    <option value="Football Program">Football Program</option>
                    <option value="Cheer Program">Cheer Program</option>
                    <option value="Volunteering">Volunteering</option>
                    <option value="Sponsorship">Sponsorship</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block font-sans text-xs font-semibold text-gray-700 mb-1 uppercase tracking-wide">
                    Message <span className="text-warrior-red">*</span>
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={4}
                    maxLength={500}
                    placeholder="Type your question or message here..."
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full border border-gray-200 rounded-md px-4 py-2.5 font-sans text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-warrior-red/30 focus:border-warrior-red transition resize-none"
                  />
                  <p className={`text-right font-sans text-xs mt-1 ${charCount >= 500 ? 'text-warrior-red' : 'text-gray-400'}`}>
                    {charCount}/500
                  </p>
                </div>

                {formStatus === 'error' && (
                  <p className="font-sans text-sm text-warrior-red text-center">
                    Something went wrong. Please try again or email us directly.
                  </p>
                )}

                <div className="text-center">
                  <button
                    type="submit"
                    disabled={formStatus === 'submitting' || charCount > 500}
                    className="px-10 py-3 bg-warrior-red text-white font-display text-lg rounded-md hover:bg-warrior-red/90 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {formStatus === 'submitting' ? 'SENDING...' : 'SEND MESSAGE'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>

        {/* Meet Our Team Section */}
        <div className="bg-white rounded-[32px] p-8 lg:p-12 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-32 h-32 bg-warrior-red/10 rounded-br-full"></div>
          <div className="relative z-10">
            <div className="text-center mb-10">
              <h2 className="font-display text-4xl text-warrior-black mb-2">MEET OUR TEAM</h2>
              <p className="font-sans text-sm text-gray-600">
                Dedicated leaders committed to building character and excellence in our young athletes
              </p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
              {officers.map((officer) => (
                <div key={officer.name} className="text-center">
                  <div className="w-full aspect-square rounded-2xl overflow-hidden mb-3 bg-gray-100">
                    <img
                      src={officer.image}
                      alt={officer.name}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <h3 className="font-display text-lg text-warrior-black mb-1">{officer.name}</h3>
                  <p className="font-sans text-xs text-gray-600 leading-snug">{officer.title}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Main Footer Card */}
        <div className="bg-white rounded-[32px] p-8 lg:p-12 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-32 h-32 bg-warrior-red/10 rounded-br-full"></div>
          <div className="absolute bottom-0 right-0 w-40 h-40 bg-warrior-black/5 rounded-tl-full"></div>

          <div className="relative z-10 grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {/* Column 1 - About */}
            <div className="lg:col-span-1">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center">
                  <img
                    src="https://static.readdy.ai/image/ad8804c8cf5327b346b1700de5ce7056/3a284c370a15b2f5c58dd5f7c0a663aa.png"
                    alt="Cherokee Warriors Logo"
                    className="w-full h-full object-contain"
                  />
                </div>
                <div className="flex flex-col">
                  <span className="font-display text-lg text-warrior-black leading-none">CHEROKEE</span>
                  <span className="font-display text-base text-warrior-red leading-none">WARRIORS</span>
                </div>
              </div>
              <p className="font-sans text-sm text-gray-600 leading-relaxed mb-4">
                Building character, teamwork, and confidence in young athletes through football and cheerleading programs in Canton, GA.
              </p>
              <p className="font-sans text-xs text-gray-500 italic">
                A 501(c)(3) Non-Profit Organization via affiliation with Cherokee Warriors Touchdown Club
              </p>
            </div>

            {/* Column 2 - Programs */}
            <div>
              <h4 className="font-display text-xl text-warrior-black mb-4">PROGRAMS</h4>
              <ul className="space-y-2 font-sans text-sm text-gray-600">
                <li><a href="#programs" className="hover:text-warrior-red transition-colors cursor-pointer">Football (K-5)</a></li>
                <li><a href="#programs" className="hover:text-warrior-red transition-colors cursor-pointer">Cheer (K-5)</a></li>
                <li>
                  <button onClick={() => navigate('/events#summer-camps')} className="hover:text-warrior-red transition-colors cursor-pointer text-left">
                    Summer Camps
                  </button>
                </li>
                <li>
                  <button onClick={() => navigate('/events#skills-clinics')} className="hover:text-warrior-red transition-colors cursor-pointer text-left">
                    Skills Clinics
                  </button>
                </li>
              </ul>
            </div>

            {/* Column 3 - Resources */}
            <div>
              <h4 className="font-display text-xl text-warrior-black mb-4">RESOURCES</h4>
              <ul className="space-y-2 font-sans text-sm text-gray-600">
                <li>
                  <a 
                    href="https://www.cobbfootballleague.com/cfl/2025CobbFootballLeagueRulesFINAL.pdf" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="hover:text-warrior-red transition-colors cursor-pointer"
                  >
                    CFL Rules
                  </a>
                </li>
                <li>
                  <a href="/volunteer" className="hover:text-warrior-red transition-colors cursor-pointer text-left">
                    Volunteer Info
                  </a>
                </li>
                <li>
                  <button onClick={() => navigate('/sponsors')} className="hover:text-warrior-red transition-colors cursor-pointer text-left">
                    Sponsors
                  </button>
                </li>
                <li>
                  <a href="https://www.cherokeewarriorsfootball.org/" target="_blank" rel="nofollow noopener noreferrer" className="hover:text-warrior-red transition-colors cursor-pointer">
                    High School Booster Club
                  </a>
                </li>
              </ul>
            </div>

            {/* Column 4 - Connect */}
            <div>
              <h4 className="font-display text-xl text-warrior-black mb-4">CONNECT</h4>
              <ul className="space-y-2 font-sans text-sm text-gray-600 mb-6">
                <li className="flex items-start space-x-2">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-map-pin-line text-base text-warrior-red"></i>
                  </div>
                  <span>Canton, GA</span>
                </li>
                <li className="flex items-start space-x-2">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-mail-line text-base text-warrior-red"></i>
                  </div>
                  <a href="mailto:contact@cherokeeyouth.com" className="hover:text-warrior-red transition-colors cursor-pointer">
                    contact@cherokeeyouth.com
                  </a>
                </li>
              </ul>

              <div className="flex space-x-3">
                <a
                  href="https://www.facebook.com/profile.php?id=61585127900574"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="w-10 h-10 flex items-center justify-center bg-warrior-black hover:bg-warrior-red rounded-full transition-colors cursor-pointer"
                >
                  <i className="ri-facebook-fill text-lg text-white"></i>
                </a>
                <a
                  href="https://instagram.com/cherokeeyouthwarriors"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="w-10 h-10 flex items-center justify-center bg-warrior-black hover:bg-warrior-red rounded-full transition-colors cursor-pointer"
                >
                  <i className="ri-instagram-line text-lg text-white"></i>
                </a>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="relative z-10 pt-8 border-t border-gray-200">
            <p className="font-sans text-sm text-gray-600 text-center md:text-left">
              © {currentYear} Cherokee Youth Football & Cheer |{' '}
              <a href="#" className="hover:text-warrior-red transition-colors cursor-pointer">Privacy Policy</a> |{' '}
              <a href="#" className="hover:text-warrior-red transition-colors cursor-pointer">Terms of Use</a>
            </p>
          </div>
        </div>

      </div>
    </footer>
  );
}